import { useEffect, useMemo, useState } from 'react'

import { Loading } from '@hhgtech/hhg-components'
import { Button, Text } from '@hhgtech/hhg-components/mantine'
import { ArrowRight, Info } from '@hhgtech/icons/other'
import { Box, BoxProps, Flex, Tooltip } from '@mantine/core'
import dayjs from 'dayjs'
import { useIntl } from 'react-intl'

import { callApiNew } from 'api/axios'
import { formatBMIResult, formatTHRResult } from 'api/dataTransform'
import { PATHS } from 'api/paths'
import { getHelloSitesUrl } from 'api/utils/paths'
import { getThrResult } from 'components/molecules/thrIllustrator/helper'
import { useAppContext } from 'hooks/useAppContext'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { API_DATE_FORMAT } from 'interfaces/constants'
import { HEALTH_TOOL_NAME } from 'interfaces/constants/healthTools'
import { HealthTool } from 'interfaces/types'

import useStyles from './index.styles'
import { ResultCard } from './resultCard'
import { generateBmiLevel } from '../../bmi/helpers'
import { checkBMRStatus } from '../../helpers'

type Props = {
  data?: HealthTool
  color?: string
  loading?: boolean
  healthData?: any
  trackingName: string
} & BoxProps

export const ToolCard = ({
  data,
  healthData,
  // loading: loadingProp = false,
  color,
  styles,
  classNames,
  className,
  trackingName,
  ...boxProps
}: Props) => {
  const {
    state: { isLoggedIn },
  } = useAppContext()
  const { locale } = useRouterExtend()
  const { formatMessage: f } = useIntl()
  const [loading, setLoading] = useState(true)
  const hasResult = useMemo(() => Boolean(isLoggedIn && healthData), [
    isLoggedIn,
    healthData,
  ])
  const isMobile = useIsMobile()
  const { classes, cx } = useStyles(
    {
      color,
      hasResult,
    },
    {
      name: 'IntroToolsBanner__ToolCard',
      styles,
      classNames,
    },
  )
  const [status, setStatus] = useState<{
    score: number
    status: string
    color?: string
    unit?: string
  }>({
    score: 0,
    status: '',
    color: '',
    unit: '',
  })

  useEffect(() => {
    if (healthData) {
      setLoading(true)
      data?.template === HEALTH_TOOL_NAME.BMI
        ? (async () => {
            const bmiResult = await callApiNew(
              PATHS.HEALTH_TOOL.GET_HEALTH_TOOL_BMI_RESULT,
              {
                params: {
                  age: healthData.age,
                  gender: 'female',
                  level: healthData.score_level,
                  bmi: healthData.score,
                },
              },
            )
            const bmiLevels = generateBmiLevel({
              f,
              gender: 'female',
              bmiValue: healthData.score,
              bmiRange: formatBMIResult(bmiResult?.data || {}).bmiRange,
            })
            const activeCondition = (bmiLevels.conditions || []).find(
              ({ isActive }) => isActive,
            )
            setStatus({
              score: healthData.score,
              status: activeCondition?.title || '',
              color: activeCondition?.color,
            })
          })()
        : data?.template === HEALTH_TOOL_NAME.BMR
        ? (async () => {
            const bmrState = checkBMRStatus({
              bmr: healthData.kcal,
              gender: 'female',
            })
            const resultRes = await callApiNew(
              PATHS.HEALTH_TOOL.GET_HEALTH_TOOL_BMR_RESULT,
              {
                params: {
                  kcals: healthData.kcal,
                  age: healthData.age,
                  gender: 'female',
                  status: bmrState,
                },
              },
            )
            const bmrResult = formatBMIResult(
              resultRes?.data || {},
              f({ id: `bmr.state.${bmrState}` }),
            )
            setStatus({
              score: healthData.kcal,
              status: bmrResult?.statusText,
              color: '',
              unit: 'kcal',
            })
          })()
        : data?.template === HEALTH_TOOL_NAME.THR
        ? (async () => {
            const thrResult = await callApiNew(
              PATHS.HEALTH_TOOL.GET_HEALTH_TOOL_THR_RESULT,
              {
                params: {
                  gender: 'female',
                  age: healthData.birthday,
                  rate: healthData.heart_rate_resting,
                  intensity: healthData.physical_activity_assessment,
                },
              },
            )
            const thrResultFormat = formatTHRResult(thrResult?.data || {})
            const { result } = getThrResult(
              healthData.birthday,
              healthData.heart_rate_resting,
              thrResultFormat?.restingHeartRate?.chart,
              f,
              thrResultFormat?.restingHeartRate?.status,
            )
            setStatus({
              score: healthData.heart_rate_resting,
              status: result.title,
              color: result.color,
              unit: 'bmp',
            })
          })()
        : undefined
    }
  }, [healthData])

  useEffect(() => {
    status?.score && setLoading(false)
  }, [status])

  return data ? (
    <Box
      className={cx(className, classes.root)}
      {...boxProps}
      component={isMobile ? 'a' : 'div'}
      {...(isMobile && {
        href: data.link.replace(/health-tools\/(?=health-tools\/)/, ''),
        target: '_blank',
        'data-event-category': 'Health Tool',
        'data-event-action': `Curated - Health Tool Click`,
        'data-event-label': data.link.replace(
          /health-tools\/(?=health-tools\/)/,
          '',
        ),
      })}
    >
      <Box className={classes.info}>
        <Box className={classes.topInfo}>
          <Box className={classes.header}>
            <img
              loading="lazy"
              src={data.icon}
              className={classes.img}
              alt={data.title}
            />
            <Text size="s3" className={classes.title}>
              {data.title}
            </Text>
          </Box>
          {hasResult &&
            (isMobile ? (
              <></>
            ) : (
              // <Text size="n1" className={classes.updatedBy}>
              //   {f(
              //     {
              //       id: 'healthToolsPage.updatedOn',
              //     },
              //     {
              //       date: dayjs(healthData.updated_at, API_DATE_FORMAT).format(
              //         'DD/MM/YYYY',
              //       ),
              //     },
              //   )}
              // </Text>
              <Tooltip
                withinPortal
                withArrow
                label={f(
                  {
                    id: 'healthToolsPage.updatedOn',
                  },
                  {
                    date: dayjs(healthData.updated_at, API_DATE_FORMAT).format(
                      'DD/MM/YYYY',
                    ),
                  },
                )}
              >
                <Button variant="white" className={classes.tooltipBtn}>
                  <Info color={'#737373'} size={isMobile ? 18 : 20} />
                </Button>
              </Tooltip>
            ))}
        </Box>
        {hasResult ? (
          <Box sx={{ position: 'relative' }}>
            {loading && (
              <Flex
                align={'flex-start'}
                justify={'center'}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  '.loading': {
                    padding: 0,
                  },
                }}
              >
                <Loading className="loading" />
              </Flex>
            )}
            <ResultCard
              score={status.score}
              status={status.status}
              color={status.color}
              unit={status.unit}
              sx={{
                opacity: Number(!loading),
              }}
            />
            <Button
              variant="tertiary"
              size="sm"
              rightIcon={<ArrowRight size={18} color="currentColor" />}
              className={cx(classes.recalcBtn, classes.btn)}
              data-event-category="Health Tool"
              data-event-action={`Curated - Health Tool Click`}
              data-event-label={`${getHelloSitesUrl(locale)}${data.link}`}
              onClick={() =>
                (window.location.href = data.link.replace(
                  /health-tools\/(?=health-tools\/)/,
                  '',
                ))
              }
              sx={{
                opacity: Number(!loading),
              }}
            >
              {!isMobile && f({ id: 'tools.recalculate' })}
            </Button>
          </Box>
        ) : (
          <Text size="p4" className={classes.desc} lineClamp={3}>
            {data.subtitle}
          </Text>
        )}
      </Box>
      {!hasResult && (
        <Box className={classes.action}>
          <Button
            variant="tertiary"
            size="sm"
            rightIcon={<ArrowRight size={18} color="currentColor" />}
            className={classes.btn}
            data-event-category="Health Tool"
            data-event-action={`Curated - Health Tool Click`}
            data-event-label={`${getHelloSitesUrl(locale)}${data.link}`}
            onClick={() =>
              (window.location.href = data.link.replace(
                /health-tools\/(?=health-tools\/)/,
                '',
              ))
            }
          >
            {!isMobile && f({ id: 'check.now' })}
          </Button>
        </Box>
      )}
    </Box>
  ) : null
}
